/**
 * Detects merge-field placeholders in email template HTML and flags tokens
 * that the send pipeline does not replace.
 */

const PLACEHOLDER_NOISE = new Set([
  "aboutus",
  "africa",
  "africamideast",
  "al",
  "asia",
  "aside",
  "autologin",
  "center",
  "chap1",
  "chap2",
  "chap3",
  "chap4",
  "chap5",
  "chap6",
  "chap7",
  "chap8",
  "comment",
  "converted",
  "countries",
  "default",
  "document",
  "documents",
  "documentse",
  "europe",
  "feature1",
  "feature2",
  "feature3",
  "footer",
  "header",
  "ieooui",
  "Images",
  "jurisdiction",
  "latin",
  "le",
  "list",
  "Map",
  "Map2",
  "Map3",
  "Map4",
  "Map5",
  "Map6",
  "Map7",
  "Map8",
  "Map9",
  "Map10",
  "Map2Map",
  "Map3Map",
  "Map5Map",
  "MapMap",
  "members",
  "MessageViewBody",
  "miamigroups",
  "name",
  "Name",
  "north",
  "NorthAmerica",
  "ondemand",
  "one",
  "opglogin",
  "part",
  "picture",
  "search",
  "surveylink",
  "three",
  "two",
  "unsubscribe",
  "VML",
  "wsg2006",
  "WSGCollabs",
  "WSGLondon2012",
  "xxx",
  "redemail",
]);

/** Normalized keys (lowercase) for supported placeholders. */
const SUPPORTED_PLACEHOLDER_KEYS = new Set([
  // {{...}} HTML / Beefree
  "firstname",
  "lastname",
  "firmname",
  "membername",
  "email",
  "unsubscribeurl",
  "unsubscribeallurl",
  // Legacy + campaign merge fields ({{}} and #)
  "company",
  "firmid",
  "first",
  "middle",
  "honorific",
  "last",
  "address",
  "city",
  "zip",
  "state",
  "country",
  "complink",
  "loglink",
  "gpnmae",
  "gpname",
  "gpid",
  "contid",
  // [#...#]
  "bannerimage",
  "prepaidmsg",
  "pendingprepaid",
  "prepaidpending",
  "fullname",
  "eventname",
  "eventtitle",
  "location",
  "eventdate",
  "paymentlink",
  "paymethod",
  "guestnamesection",
  "guestname",
  "firmname",
  "paystatus",
  "amtpaid",
  "totalamount",
  "paybalance",
  "specialeventsinfo",
  "additional events",
]);

function normalizeKey(token: string): string {
  return token.trim().toLowerCase();
}

function isSupported(token: string): boolean {
  const key = normalizeKey(token);
  if (PLACEHOLDER_NOISE.has(key) || PLACEHOLDER_NOISE.has(token)) {
    return true;
  }
  if (/^[0-9a-f]{3,8}$/i.test(key)) {
    return true;
  }
  return SUPPORTED_PLACEHOLDER_KEYS.has(key);
}

function formatDisplayToken(kind: "bracket" | "closed" | "bare" | "mustache", raw: string): string {
  switch (kind) {
    case "bracket":
      return `[#${raw}#]`;
    case "closed":
      return `#${raw}#`;
    case "mustache":
      return `{{${raw}}}`;
    default:
      return `#${raw}`;
  }
}

/**
 * Returns distinct unsupported placeholder strings found in template HTML.
 */
export function findUnsupportedEmailPlaceholders(html: string): string[] {
  if (!html || typeof html !== "string") {
    return [];
  }

  const unsupported = new Set<string>();

  for (const match of html.matchAll(/\[#\s*([A-Za-z][A-Za-z0-9 _]{0,60})\s*#\]/g)) {
    const raw = match[1].trim();
    if (!isSupported(raw)) {
      unsupported.add(formatDisplayToken("bracket", raw));
    }
  }

  for (const match of html.matchAll(/(?<!\[)#([A-Za-z][A-Za-z0-9_]{1,40})#(?!\])/g)) {
    const raw = match[1];
    if (!isSupported(raw)) {
      unsupported.add(formatDisplayToken("closed", raw));
    }
  }

  for (const match of html.matchAll(
    /(?:^|[^A-Za-z0-9_\[#])#([A-Za-z][A-Za-z0-9_]{1,40})(?![A-Za-z0-9_#])/g
  )) {
    const raw = match[1];
    if (!isSupported(raw)) {
      unsupported.add(formatDisplayToken("bare", raw));
    }
  }

  for (const match of html.matchAll(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g)) {
    const raw = match[1];
    if (!isSupported(raw)) {
      unsupported.add(formatDisplayToken("mustache", raw));
    }
  }

  return [...unsupported].sort((a, b) => a.localeCompare(b));
}

export function unsupportedPlaceholderWarningMessage(tokens: string[]): string {
  if (tokens.length === 0) {
    return "";
  }
  const preview =
    tokens.length <= 8 ? tokens.join(", ") : `${tokens.slice(0, 8).join(", ")} (+${tokens.length - 8} more)`;
  return `This template contains placeholders that may not be replaced when sending: ${preview}. Consider updating them to supported merge fields (e.g. {{first}}, {{company}}, {{Email}}).`;
}
